import React, { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import type { UseFormReturn, FieldValues } from 'react-hook-form';

interface CriticalElementFormContextValue extends UseFormReturn<FieldValues> {
  loading?: boolean;
}

const CriticalElementFormContext = createContext<
  CriticalElementFormContextValue | undefined
>(undefined);

export const useCriticalElementForm = (): CriticalElementFormContextValue => {
  const ctx = useContext(CriticalElementFormContext);

  if (!ctx) {
    throw new Error(
      'useCriticalElementForm must be used within CriticalElementFormProvider'
    );
  }

  return ctx;
};

interface CriticalElementFormProviderProps {
  children: ReactNode;
  defaultValues: FieldValues;
  loading?: boolean;
}

export const CriticalElementFormProvider = ({
  children,
  defaultValues,
  loading,
}: CriticalElementFormProviderProps): React.JSX.Element => {
  const methods = useForm({
    defaultValues,
  });

  const contextValue = useMemo<CriticalElementFormContextValue>(
    () => ({ ...methods, loading }),
    [methods, loading]
  );

  return (
    <CriticalElementFormContext.Provider value={contextValue}>
      {children}
    </CriticalElementFormContext.Provider>
  );
};
